import { useCallback, useRef } from 'react';

import { LONG_PRESS_DELAY_MS, LONG_PRESS_VIBRATION_MS } from './constants';

/**
 * Disambiguates a short tap from a long press on a pointer-based element.
 *
 * - Short press (< LONG_PRESS_DELAY_MS): calls `onTap`.
 * - Long press (≥ LONG_PRESS_DELAY_MS): fires haptic feedback + calls `onLongPress`.
 * - Pointer leave / cancel: cancels a pending long-press timer.
 *
 * Returns an object of pointer event handlers to spread onto the target element.
 *
 * @param {Object} params
 * @param {Function} params.onTap - called on a short press
 * @param {Function} params.onLongPress - called after holding ≥ LONG_PRESS_DELAY_MS
 * @returns {{ onPointerDown: Function, onPointerUp: Function, onPointerLeave: Function, onPointerCancel: Function }}
 */
const useLongPress = ({ onTap, onLongPress }) => {
  const timerRef = useRef(null);
  const didFireRef = useRef(false);

  const cancelTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const onPointerDown = useCallback(
    event => {
      event.preventDefault();
      didFireRef.current = false;

      timerRef.current = setTimeout(() => {
        didFireRef.current = true;
        timerRef.current = null;

        if (navigator.vibrate) {
          navigator.vibrate(LONG_PRESS_VIBRATION_MS);
        }

        onLongPress();
      }, LONG_PRESS_DELAY_MS);
    },
    [onLongPress],
  );

  const onPointerUp = useCallback(() => {
    cancelTimer();

    if (!didFireRef.current) {
      onTap();
    }
  }, [cancelTimer, onTap]);

  const onPointerLeave = useCallback(() => {
    cancelTimer();
  }, [cancelTimer]);

  const onPointerCancel = useCallback(() => {
    cancelTimer();
  }, [cancelTimer]);

  return { onPointerDown, onPointerUp, onPointerLeave, onPointerCancel };
};

export default useLongPress;
